import { EliteScoreTrends, PowerPerformanceForms, KnowWhyAPIReponseType } from '../../types/mutual-fund-detail.types'
import { GradientStripType } from '../ui-components/GradientStrip'

export interface ScoreConfig {
  scoreLabel: string
  gradientStripType: GradientStripType
  backgroundColor: string
  textColor: string
  gradientColor: string
  lockColor: string
  leafColor?: string // For power forms only
}

export const SCORE_CONFIG: Record<EliteScoreTrends, ScoreConfig> = {
  [EliteScoreTrends.GREAT]: {
    scoreLabel: 'Great',
    gradientStripType: GradientStripType.GREEN,
    backgroundColor: '#CBF7D7',
    textColor: '#00661D',
    gradientColor: '#CBF7D7',
    lockColor: '#00661D',
    leafColor: '#7AEB9A' // IN_FORM
  },
  [EliteScoreTrends.GOOD]: {
    scoreLabel: 'Good',
    gradientStripType: GradientStripType.GREEN,
    backgroundColor: '#E4FCEB',
    textColor: '#00661D',
    gradientColor: '#E4FCEB',
    lockColor: '#00661D',
    leafColor: '#ACE5BD' // ON_TRACK
  },
  [EliteScoreTrends.AVERAGE]: {
    scoreLabel: 'Average',
    gradientStripType: GradientStripType.ORANGE,
    backgroundColor: '#FFF4E6',
    textColor: '#995400',
    gradientColor: '#FFF4E6',
    lockColor: '#995400',
    leafColor: '#F5CD9D' // OFF_TRACK
  },
  [EliteScoreTrends.BAD]: {
    scoreLabel: 'Bad',
    gradientStripType: GradientStripType.RED,
    backgroundColor: '#FFE9E6',
    textColor: '#992617',
    gradientColor: '#FFE9E6',
    lockColor: '#992617',
    leafColor: '#F3B9B1' // OUT_OF_FORM
  }
}

// Helper to get config for a tag
export const getScoreConfig = (tag: EliteScoreTrends): ScoreConfig => {
  return SCORE_CONFIG[tag] || SCORE_CONFIG[EliteScoreTrends.GOOD]
}

export const getTagFromPowerForm = (powerForm: `${PowerPerformanceForms}`): EliteScoreTrends => {
  switch (powerForm) {
    case PowerPerformanceForms.IN_FORM:
      return EliteScoreTrends.GREAT
    case PowerPerformanceForms.ON_TRACK:
      return EliteScoreTrends.GOOD
    case PowerPerformanceForms.OFF_TRACK:
      return EliteScoreTrends.AVERAGE
    case PowerPerformanceForms.OUT_OF_FORM:
      return EliteScoreTrends.BAD
    default:
      return EliteScoreTrends.GOOD
  }
}

export const getConfigFromPowerForm = (powerForm: `${PowerPerformanceForms}`): ScoreConfig => {
  return SCORE_CONFIG[getTagFromPowerForm(powerForm)]
}

// ==================== Score Sections Config ====================

export enum ScoreSectionType {
  POWER_RANK = 'POWER_RANK',
  CONSISTENCY_SCORE = 'CONSISTENCY_SCORE',
  RECENCY_SCORE = 'RECENCY_SCORE',
  VOLATILITY_SCORE = 'VOLATILITY_SCORE'
}

// Default order of sections
export const DEFAULT_SCORE_SECTIONS: ScoreSectionType[] = [
  ScoreSectionType.POWER_RANK,
  ScoreSectionType.CONSISTENCY_SCORE,
  ScoreSectionType.RECENCY_SCORE,
  ScoreSectionType.VOLATILITY_SCORE
]

const isTagUnknown = (tag: `${EliteScoreTrends}` | null | undefined | string): boolean => {
  return !tag || tag === 'UNKNOWN'
}

const isScoreAvailable = (
  score: number | null | undefined,
  tag: `${EliteScoreTrends}` | null | undefined | string
): boolean => {
  return !isTagUnknown(tag)
}

// Map section types to their data fields for validation
const sectionDataFieldMap: Record<ScoreSectionType, (data: KnowWhyAPIReponseType) => boolean> = {
  [ScoreSectionType.POWER_RANK]: () => true,
  [ScoreSectionType.CONSISTENCY_SCORE]: (data) =>
    isScoreAvailable(data?.scoringDetails?.consistencyScore, data?.scoringDetails?.consistencyScorePerformanceTag),
  [ScoreSectionType.RECENCY_SCORE]: (data) =>
    isScoreAvailable(data?.scoringDetails?.recencyScore, data?.scoringDetails?.recencyScorePerformanceTag),
  [ScoreSectionType.VOLATILITY_SCORE]: (data) =>
    isScoreAvailable(data?.scoringDetails?.volatilityScore, data?.scoringDetails?.volatilityScorePerformanceTag)
}

// Filter function to conditionally remove sections based on data availability
export const getScoreSections = (knowWhyData: KnowWhyAPIReponseType): ScoreSectionType[] => {
  return DEFAULT_SCORE_SECTIONS.filter((sectionType) => {
    const hasData = sectionDataFieldMap[sectionType]
    return hasData ? hasData(knowWhyData) : true
  })
}
